import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import pptxgen from "pptxgenjs";

const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c])
  );

const expandScrollables = (root) => {
  const changed = [];
  const els = [root, ...root.querySelectorAll("*")];
  els.forEach((el) => {
    const cs = window.getComputedStyle(el);
    const scrollableX =
      (cs.overflowX === "auto" || cs.overflowX === "scroll") &&
      el.scrollWidth > el.clientWidth + 1;
    const scrollableY =
      (cs.overflowY === "auto" || cs.overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight + 1;
    if (!scrollableX && !scrollableY) return;

    changed.push({
      el,
      overflow: el.style.overflow,
      overflowX: el.style.overflowX,
      overflowY: el.style.overflowY,
      width: el.style.width,
      maxWidth: el.style.maxWidth,
      height: el.style.height,
      maxHeight: el.style.maxHeight,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
    });
    el.style.overflow = "visible";
    el.style.overflowX = "visible";
    el.style.overflowY = "visible";
    if (scrollableX) {
      el.style.width = el.scrollWidth + "px";
      el.style.maxWidth = "none";
    }
    if (scrollableY) {
      el.style.height = el.scrollHeight + "px";
      el.style.maxHeight = "none";
    }
    el.scrollLeft = 0;
    el.scrollTop = 0;
  });
  return changed;
};

const restoreScrollables = (changed) => {
  changed.forEach((c) => {
    c.el.style.overflow = c.overflow;
    c.el.style.overflowX = c.overflowX;
    c.el.style.overflowY = c.overflowY;
    c.el.style.width = c.width;
    c.el.style.maxWidth = c.maxWidth;
    c.el.style.height = c.height;
    c.el.style.maxHeight = c.maxHeight;
    c.el.scrollLeft = c.scrollLeft;
    c.el.scrollTop = c.scrollTop;
  });
};

const getBlockBounds = (element, canvas) => {
  const containerRect = element.getBoundingClientRect();
  const scale = canvas.width / containerRect.width;
  return Array.from(element.querySelectorAll(".MuiCard-root")).map((el) => {
    const r = el.getBoundingClientRect();
    return {
      top: (r.top - containerRect.top) * scale,
      bottom: (r.bottom - containerRect.top) * scale,
    };
  });
};

const captureElement = async (element) => {
  const noAnim = document.createElement("style");
  noAnim.textContent =
    "*{animation:none !important;transition:none !important;}";
  document.head.appendChild(noAnim);

  const changed = expandScrollables(element);

  try {
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch (e) {}
    }
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r))
    );
    await new Promise((r) => setTimeout(r, 350));

    const fullWidth = Math.max(element.scrollWidth, element.offsetWidth);
    const fullHeight = element.scrollHeight;
    // adaptive scale: กัน canvas เกิน limit ของ browser (โดยเฉพาะ Safari) → capture ได้ภาพว่าง
    const MAX_DIM = 10000;
    const scale = Math.max(1, Math.min(2.5, MAX_DIM / Math.max(fullWidth, fullHeight)));

    const opts = {
      scale,
      useCORS: true,
      backgroundColor: "#ffffff",
      imageTimeout: 15000, // ไม่ค้างถ้ารูปโหลดช้า
    };
    // set width/windowWidth เฉพาะตอนขยายตาราง scroll เท่านั้น (กัน MUI Grid reflow ในเคสปกติ → ภาพตรงจอ)
    if (changed.length > 0) {
      opts.width = fullWidth;
      opts.windowWidth = fullWidth;
    }
    const canvas = await html2canvas(element, opts);
    const blocks = getBlockBounds(element, canvas);
    return { canvas, blocks };
  } finally {
    restoreScrollables(changed);
    if (noAnim.parentNode) noAnim.parentNode.removeChild(noAnim);
  }
};

const computePageSlices = (blocks, canvas, pageSlicePx) => {
  const H = canvas.height;
  const straddles = (y) =>
    blocks.some((b) => b.top < y - 0.5 && y + 0.5 < b.bottom);

  const slices = [];
  let y = 0;
  let guard = 0;
  while (y < H - 1 && guard++ < 2000) {
    const target = y + pageSlicePx;
    if (target >= H) {
      slices.push([y, H]);
      break;
    }
    let candidates = [];
    for (const b of blocks) {
      if (b.top > y && b.top <= target) candidates.push(b.top);
      if (b.bottom > y && b.bottom <= target) candidates.push(b.bottom);
    }
    candidates.push(target);
    candidates = candidates
      .filter((c) => c > y && c <= target && !straddles(c))
      .sort((a, b) => a - b);

    let cut = candidates.length ? candidates[candidates.length - 1] : target;
    if (cut <= y) cut = target;
    slices.push([y, cut]);
    y = cut;
  }
  return slices;
};

const sliceToDataURL = (canvas, startY, endY, mime = "image/png", quality) => {
  const h = Math.max(1, Math.round(endY - startY));
  const tmp = document.createElement("canvas");
  tmp.width = canvas.width;
  tmp.height = h;
  const ctx = tmp.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, tmp.width, tmp.height);
  ctx.drawImage(canvas, 0, Math.round(startY), canvas.width, h, 0, 0, canvas.width, h);
  return { dataURL: tmp.toDataURL(mime, quality), heightPx: h };
};

const buildHeaderCanvas = async ({ title, subtitle }) => {
  const div = document.createElement("div");
  div.style.cssText =
    "position:fixed;left:-10000px;top:0;width:1100px;background:#ffffff;" +
    "font-family:'Sarabun','Prompt','Kanit',sans-serif;";
  div.innerHTML = `
    <div style="display:flex;align-items:flex-end;justify-content:space-between;
                border-bottom:3px solid #007afe;padding:4px 4px 12px;">
      <div>
        <div style="font-size:30px;font-weight:700;color:#1f2937;line-height:1.2;">${esc(title)}</div>
        <div style="font-size:18px;color:#6b7280;margin-top:6px;">${esc(subtitle)}</div>
      </div>
      <div style="font-size:24px;font-weight:700;color:#007afe;">AntHR</div>
    </div>`;
  document.body.appendChild(div);
  try {
    return await html2canvas(div, { scale: 2, backgroundColor: "#ffffff" });
  } finally {
    document.body.removeChild(div);
  }
};

// ---------- PDF ----------

export const exportToPDF = async (
  element,
  { fileName = "dashboard", title = "", subtitle = "" } = {}
) => {
  const { canvas, blocks } = await captureElement(element);
  const headerCanvas = await buildHeaderCanvas({ title, subtitle });

  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const margin = 32;
  const footerH = 22;
  const contentW = pageW - margin * 2;
  const headerH = contentW * (headerCanvas.height / headerCanvas.width);
  const contentTop = margin + headerH + 12;
  const contentAvailH = pageH - contentTop - footerH - margin;

  const pxPerPt = canvas.width / contentW; 
  const pageSlicePx = contentAvailH * pxPerPt;

  const slices = computePageSlices(blocks, canvas, pageSlicePx);
  const headerData = headerCanvas.toDataURL("image/png");

  slices.forEach(([s, e], i) => {
    if (i > 0) pdf.addPage();
    pdf.addImage(headerData, "PNG", margin, margin, contentW, headerH);
    const { dataURL, heightPx } = sliceToDataURL(canvas, s, e);
    pdf.addImage(dataURL, "PNG", margin, contentTop, contentW, heightPx / pxPerPt);
    pdf.setFontSize(10);
    pdf.setTextColor(150);
    pdf.text(`${i + 1} / ${slices.length}`, pageW / 2, pageH - margin / 2, {
      align: "center",
    });
  });
  pdf.save(`${fileName}.pdf`);
};

// ---------- PPT ----------

export const exportToPPT = async (
  element,
  { fileName = "dashboard", title = "", subtitle = "" } = {}
) => {
  const { canvas, blocks } = await captureElement(element);

  const pres = new pptxgen();
  pres.layout = "LAYOUT_WIDE";
  const slideW = 13.33;
  const slideH = 7.5;

  const margin = 0.4;
  const headerH = 0.95;
  const footerH = 0.3;
  const contentTop = margin + headerH;
  const contentW = slideW - margin * 2;
  const contentAvailH = slideH - contentTop - footerH;

  const pxPerInch = canvas.width / contentW;
  const pageSlicePx = contentAvailH * pxPerInch;

  const slices = computePageSlices(blocks, canvas, pageSlicePx);

  slices.forEach(([s, e], i) => {
    const slide = pres.addSlide();
    slide.addText(title, {
      x: margin, y: 0.25, w: contentW - 1.6, h: 0.45,
      fontSize: 22, bold: true, color: "1F2937", fontFace: "Tahoma",
    });
    slide.addText(subtitle, {
      x: margin, y: 0.68, w: contentW - 1.6, h: 0.3,
      fontSize: 13, color: "6B7280", fontFace: "Tahoma",
    });
    slide.addText("AntHR", {
      x: slideW - margin - 1.5, y: 0.25, w: 1.5, h: 0.45,
      fontSize: 18, bold: true, color: "007AFE", fontFace: "Tahoma", align: "right",
    });
    slide.addShape(pres.ShapeType.line, {
      x: margin, y: contentTop - 0.1, w: contentW, h: 0,
      line: { color: "007AFE", width: 2 },
    });
    // PPT ใช้ JPEG → ไฟล์ .pptx เล็กลงมาก (ยังเป็น PPT เหมือนเดิม)
    const { dataURL, heightPx } = sliceToDataURL(canvas, s, e, "image/jpeg", 0.9);
    slide.addImage({
      data: dataURL,
      x: margin, y: contentTop, w: contentW, h: heightPx / pxPerInch,
    });
    slide.addText(`${i + 1} / ${slices.length}`, {
      x: slideW - margin - 1, y: slideH - footerH, w: 1, h: footerH,
      fontSize: 10, color: "9CA3AF", align: "right", fontFace: "Tahoma",
    });
  });
  await pres.writeFile({ fileName: `${fileName}.pptx` });
};
